import { supabase } from '@/lib/integrations/supabase'

export type EntityMediaType = 'image' | 'video' | 'pdf' | 'document' | 'spreadsheet' | 'other'

export interface EntityMedia {
  id: string
  entity_id: string
  unit_id: string
  media_url: string
  media_name: string
  media_type: EntityMediaType
  mime_type: string
  size_bytes: number
  metadata: Record<string, unknown>
  created_at: string
  created_by: string | null
  updated_at: string
}

export interface EntityMediaUploadResult {
  id: string
  media_name: string
  media_url: string
  media_type: EntityMediaType
  url: string
}

export interface EntityMediaPreview {
  id: string
  entity_id: string
  media_name: string
  media_type: EntityMediaType
  media_url: string
  signed_url: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface EntityMediaSummary {
  count: number
  previews: EntityMediaPreview[]
}

export interface EntityMediaServiceConfig {
  table?: string
  bucket?: string
  entityColumn?: string
  scopeColumn?: string
  scopeValue?: string
  pathColumn?: string
  nameColumn?: string
  typeColumn?: string
  metadataColumn?: string
  typeValues?: EntityMediaType[]
  resolveFileType?: (file: File) => EntityMediaType
  maxBytes?: number
  allowedTypes?: string[]
  allowedExtensions?: string[]
  serviceLabel?: string
  summaryPreviewLimit?: number
}

export interface UploadEntityMediaInput {
  unitId: string
  entityId: string
  file: File
  mediaName?: string
}

const DEFAULT_MAX_BYTES = 50 * 1024 * 1024
const DEFAULT_TABLE = 'entity_attachments'
const DEFAULT_BUCKET = 'entity-attachments'
const DEFAULT_ENTITY_COLUMN = 'entity_id'
const DEFAULT_SCOPE_COLUMN = 'entity_type'
const DEFAULT_PATH_COLUMN = 'attachment_url'
const DEFAULT_NAME_COLUMN = 'attachment_name'
const DEFAULT_TYPE_COLUMN = 'attachment_type'
const DEFAULT_METADATA_COLUMN = 'metadata'
const DEFAULT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
]

type EntityMediaRow = EntityMedia & Record<string, unknown>

function normalizeMetadata(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

function isFavorite(row: EntityMediaRow, metadataColumn: string) {
  return normalizeMetadata(row[metadataColumn]).favorite === true
}

function sortFavoriteFirst(rows: EntityMediaRow[], metadataColumn: string) {
  return [...rows].sort((a, b) => {
    const favoriteDiff = Number(isFavorite(b, metadataColumn)) - Number(isFavorite(a, metadataColumn))
    if (favoriteDiff !== 0) return favoriteDiff

    return String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''))
  })
}

function mediaTypeFor(file: File): EntityMediaType {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  throw new Error('Unsupported media file type')
}

function uniqueMediaPath(unitId: string, entityId: string, fileName: string, scope?: string) {
  const ext = fileName.split('.').pop() ?? 'bin'
  const uniqueId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

  return [unitId, scope, entityId, `${uniqueId}.${ext}`].filter(Boolean).join('/')
}

export function createEntityMediaService(config: EntityMediaServiceConfig) {
  const table = config.table ?? DEFAULT_TABLE
  const bucket = config.bucket ?? DEFAULT_BUCKET
  const entityColumn = config.entityColumn ?? DEFAULT_ENTITY_COLUMN
  const maxBytes = config.maxBytes ?? DEFAULT_MAX_BYTES
  const allowedTypes = config.allowedTypes ?? DEFAULT_ALLOWED_TYPES
  const allowedExtensions = config.allowedExtensions ?? []
  const serviceLabel = config.serviceLabel ?? 'entity media'
  const pathColumn = config.pathColumn ?? DEFAULT_PATH_COLUMN
  const nameColumn = config.nameColumn ?? DEFAULT_NAME_COLUMN
  const typeColumn = config.typeColumn ?? DEFAULT_TYPE_COLUMN
  const metadataColumn = config.metadataColumn ?? DEFAULT_METADATA_COLUMN
  const scopeColumn = config.scopeColumn ?? DEFAULT_SCOPE_COLUMN
  const scopeValue = config.scopeValue
  const typeValues = config.typeValues
  const resolveFileType = config.resolveFileType ?? mediaTypeFor
  const summaryPreviewLimit = config.summaryPreviewLimit ?? 3

  const normalizeConfiguredMedia = (row: EntityMediaRow): EntityMedia => ({
    id: row.id,
    entity_id: String(row[entityColumn] ?? ''),
    unit_id: row.unit_id,
    media_url: String(row[pathColumn] ?? ''),
    media_name: String(row[nameColumn] ?? ''),
    media_type: row[typeColumn] as EntityMediaType,
    mime_type: row.mime_type,
    size_bytes: row.size_bytes,
    metadata: normalizeMetadata(row[metadataColumn]),
    created_at: row.created_at,
    created_by: row.created_by,
    updated_at: row.updated_at,
  })

  async function uploadEntityMedia({
    unitId,
    entityId,
    file,
    mediaName,
  }: UploadEntityMediaInput): Promise<EntityMediaUploadResult> {
    if (file.size > maxBytes) {
      throw new Error(`${serviceLabel} file exceeds the ${Math.round(maxBytes / 1024 / 1024)} MB limit`)
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    const isAllowedType = allowedTypes.includes(file.type)
    const isAllowedExtension = Boolean(fileExtension && allowedExtensions.includes(fileExtension))

    if (!isAllowedType && !isAllowedExtension) {
      throw new Error(`Unsupported ${serviceLabel} file type`)
    }

    const mediaType = resolveFileType(file)
    if (typeValues && !typeValues.includes(mediaType)) {
      throw new Error(`Unsupported ${serviceLabel} file type`)
    }
    const path = uniqueMediaPath(unitId, entityId, file.name, scopeValue)
    const scopedFields = scopeColumn && scopeValue ? { [scopeColumn]: scopeValue } : {}

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, file)

    if (uploadError) throw uploadError

    const { data: record, error: dbError } = await supabase
      .from(table)
      .insert({
        [entityColumn]: entityId,
        ...scopedFields,
        unit_id: unitId,
        [pathColumn]: path,
        [nameColumn]: mediaName || file.name,
        [typeColumn]: mediaType,
        mime_type: file.type,
        size_bytes: file.size,
      })
      .select()
      .single()

    if (dbError) {
      await supabase.storage.from(bucket).remove([path])
      throw new Error(`Failed to save ${serviceLabel} record: ${dbError.message}`)
    }

    const row = record as EntityMediaRow
    const { data: signedData } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600)

    return {
      id: row.id,
      media_name: String(row[nameColumn] ?? ''),
      media_url: path,
      media_type: row[typeColumn] as EntityMediaType,
      url: signedData?.signedUrl ?? '',
    }
  }

  async function getEntityMedia(entityId: string): Promise<EntityMedia[]> {
    let query = supabase
      .from(table)
      .select('*')
      .eq(entityColumn, entityId)

    if (scopeColumn && scopeValue) {
      query = query.eq(scopeColumn, scopeValue)
    }
    if (typeValues?.length) {
      query = query.in(typeColumn, typeValues)
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })

    if (error) throw error
    return sortFavoriteFirst((data ?? []) as EntityMediaRow[], metadataColumn).map(normalizeConfiguredMedia)
  }

  async function getEntityMediaSummaries(entityIds: string[]): Promise<Record<string, EntityMediaSummary>> {
    if (entityIds.length === 0) return {}

    let query = supabase
      .from(table)
      .select('*')
      .in(entityColumn, entityIds)

    if (scopeColumn && scopeValue) {
      query = query.eq(scopeColumn, scopeValue)
    }
    if (typeValues?.length) {
      query = query.in(typeColumn, typeValues)
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })

    if (error) throw error

    const summaries = Object.fromEntries(
      entityIds.map((id) => [id, { count: 0, previews: [] as EntityMediaPreview[] }]),
    )
    const previewRows: EntityMediaRow[] = []

    for (const item of sortFavoriteFirst((data ?? []) as EntityMediaRow[], metadataColumn)) {
      const entityId = String(item[entityColumn] ?? '')
      const summary = summaries[entityId] ?? { count: 0, previews: [] }
      const mediaPath = String(item[pathColumn] ?? '')
      summary.count += 1

      if (summary.previews.length < summaryPreviewLimit) {
        previewRows.push(item)
        summary.previews.push({
          id: item.id,
          entity_id: entityId,
          media_name: String(item[nameColumn] ?? ''),
          media_type: item[typeColumn] as EntityMediaType,
          media_url: mediaPath,
          signed_url: '',
          metadata: normalizeMetadata(item[metadataColumn]),
          created_at: item.created_at,
        })
      }

      summaries[entityId] = summary
    }

    if (previewRows.length === 0) return summaries

    const { data: signedUrls, error: signedError } = await supabase.storage
      .from(bucket)
      .createSignedUrls(previewRows.map((item) => String(item[pathColumn] ?? '')), 3600)

    if (signedError) throw signedError

    const signedByPath = new Map(
      (signedUrls ?? []).map((item) => [item.path, item.signedUrl ?? '']),
    )

    for (const summary of Object.values(summaries)) {
      summary.previews = summary.previews.map((preview) => ({
        ...preview,
        signed_url: signedByPath.get(preview.media_url) ?? '',
      }))
    }

    return summaries
  }

  async function getEntityMediaUrl(mediaPath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(mediaPath, 3600)

    if (error) throw error
    return data.signedUrl
  }

  async function updateEntityMediaName(mediaId: string, mediaName: string): Promise<EntityMedia> {
    const { data, error } = await supabase
      .from(table)
      .update({ [nameColumn]: mediaName })
      .eq('id', mediaId)
      .select()
      .single()

    if (error) throw error
    return normalizeConfiguredMedia(data as EntityMediaRow)
  }

  async function setEntityMediaFavorite(entityId: string, mediaId: string): Promise<EntityMedia[]> {
    const items = await getEntityMedia(entityId)

    const results = await Promise.all(items.map((item) => {
      const metadata = {
        ...item.metadata,
        favorite: item.id === mediaId,
      }

      return supabase
        .from(table)
        .update({ [metadataColumn]: metadata })
        .eq('id', item.id)
    }))

    const failed = results.find((result) => result.error)
    if (failed?.error) throw failed.error

    return getEntityMedia(entityId)
  }

  async function deleteEntityMedia(mediaId: string, mediaPath: string): Promise<void> {
    const { error: dbError } = await supabase
      .from(table)
      .delete()
      .eq('id', mediaId)

    if (dbError) throw dbError

    const { error: storageError } = await supabase.storage
      .from(bucket)
      .remove([mediaPath])

    if (storageError) console.warn(`Failed to delete ${serviceLabel} from storage:`, storageError)
  }

  return {
    uploadEntityMedia,
    getEntityMedia,
    getEntityMediaSummaries,
    getEntityMediaUrl,
    updateEntityMediaName,
    setEntityMediaFavorite,
    deleteEntityMedia,
  }
}
